import type { FieldType } from '~/utils/manifest-utils';

const CELL_BADGE_SWITCH_CONDITION = {
  key: 'assets/cell_badge_switch',
  condition_value: true,
} as const;

type CellBadgingManifestField = {
  readonly key: string;
  readonly type: FieldType;
  readonly label: string;
  readonly initial_value?: boolean | string | number;
  readonly rules?: string;
  readonly conditional_fields?: readonly {
    readonly key: string;
    readonly condition_value: boolean;
  }[];
  readonly options?: readonly {
    readonly text: string;
    readonly value: string;
  }[];
};

export type CellBadgingManifestGroup = {
  readonly group: true;
  readonly label: string;
  readonly folded: boolean;
  readonly fields: readonly CellBadgingManifestField[];
};

const CORNER_POSITION_OPTIONS = [
  { text: 'Center', value: 'center' },
  { text: 'Top Left', value: 'top_left' },
  { text: 'Top Right', value: 'top_right' },
  { text: 'Bottom Left', value: 'bottom_left' },
  { text: 'Bottom Right', value: 'bottom_right' },
];

const CELL_RIGHT_POSITION_OPTION = {
  text: 'Cell Right',
  value: 'cell_right',
};

/**
 * Cell Badging Studio group shared by cell_1_v2, cell_2_v2, and cell_3_v2.
 * PC1/PC3: corners + center, default Bottom Right, margins 0/10/10/0.
 * PC2: + Cell Right (default), margins 0/0/0/16.
 */
export function getCellBadgingManifestGroup({
  enableInitialValue,
  cellBadgeInitialValue,
  includeCellRightPosition = false,
  positionInitialValue = 'bottom_right',
  marginTop = 0,
  marginRight = 10,
  marginBottom = 10,
  marginLeft = 0,
}: {
  enableInitialValue: boolean;
  cellBadgeInitialValue: string;
  includeCellRightPosition?: boolean;
  positionInitialValue?: string;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
}): CellBadgingManifestGroup {
  return {
    group: true,
    label: 'Cell Badging',
    folded: false,
    fields: [
      {
        key: 'cell_badge_switch',
        label: 'enable',
        initial_value: enableInitialValue,
        type: 'switch',
      },
      {
        key: 'cell_badge',
        label: 'Cell badge',
        rules: 'conditional',
        initial_value: cellBadgeInitialValue,
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'uploader',
      },
      {
        key: 'cell_hover_badge_switch',
        label: 'Enable hover cell badge',
        initial_value: false,
        type: 'switch',
        rules: 'conditional',
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
      },
      {
        key: 'cell_hover_badge',
        label: 'Hover cell badge',
        rules: 'conditional',
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'uploader',
      },
      {
        key: 'cell_selected_badge_switch',
        label: 'Enable selected cell badge',
        initial_value: false,
        type: 'switch',
        rules: 'conditional',
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
      },
      {
        key: 'selected_cell_badge',
        label: 'Selected Cell badge',
        rules: 'conditional',
        initial_value: '',
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'uploader',
      },
      {
        key: 'cell_selected_and_hover_badge_switch',
        label: 'Enable selected & hover cell badge',
        initial_value: false,
        type: 'switch',
        rules: 'conditional',
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
      },
      {
        key: 'cell_selected_and_hover_badge',
        label: 'Selected & hover cell badge',
        rules: 'conditional',
        initial_value: '',
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'uploader',
      },
      {
        key: 'cell_badge_position',
        label: 'Position',
        options: includeCellRightPosition
          ? [...CORNER_POSITION_OPTIONS, CELL_RIGHT_POSITION_OPTION]
          : CORNER_POSITION_OPTIONS,
        initial_value: positionInitialValue,
        rules: 'conditional',
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'select',
      },
      {
        key: 'cell_badge_width',
        label: 'Width',
        initial_value: 24,
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'number_input',
      },
      {
        key: 'cell_badge_height',
        label: 'Height',
        initial_value: 24,
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'number_input',
      },
      {
        key: 'cell_badge_margin_top',
        label: 'Margin top',
        rules: 'conditional',
        initial_value: marginTop,
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'number_input',
      },
      {
        key: 'cell_badge_margin_right',
        label: 'Margin right',
        rules: 'conditional',
        initial_value: marginRight,
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'number_input',
      },
      {
        key: 'cell_badge_margin_bottom',
        label: 'Margin bottom',
        rules: 'conditional',
        initial_value: marginBottom,
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'number_input',
      },
      {
        key: 'cell_badge_margin_left',
        label: 'Margin left',
        rules: 'conditional',
        initial_value: marginLeft,
        conditional_fields: [CELL_BADGE_SWITCH_CONDITION],
        type: 'number_input',
      },
    ],
  };
}
